import {FC, useMemo, useState} from "react";
import { Box, Button, Stack, Tooltip, Typography } from "@mui/material";
import { Phone, PriorityHigh, WarningRounded } from '@mui/icons-material';
import {
    Contact,
    getContactName,
    getContactStatus,
    getContactUnavailability,
    getTypeOfCalls,
    ScheduledContactStatus
} from "utils/accounts";
import Highlighter from 'react-highlight-words';
import { useDispatch } from "react-redux";
import { setContactForEditStatus, setContactForStatusList } from "reducers/slices/UIReducer";
import ContactStatusAlert from "./ContactStatusAlert";
import ListIcon from "assets/ListIcon";
import {getContactDetail} from "../../../../../api/accounts";
import {updateContact} from "../../../../../reducers/slices/accountPanelReducer";
import ContactStatusArchiveDialog from "../ContactStatusArchiveDialog";

interface IProps {
  search: string,
  contact: Contact,
}

const TAKES_CALLS_COLORS: Record<string, string> = {
  Yes: '#448C3D',
  No: '#E80B0B',
  'Emergency Calls Only': '#FFB200',
  Default: '#E80B0B',
}

const ContactStatus: FC<IProps> = ({ contact, search }) => {
  const dispatch = useDispatch()
  const typesOfCalls: string = useMemo(() => {
    return getTypeOfCalls(contact)
  }, [contact])
    const [isConfirmingArchive, setIsConfirmingArchive] = useState<ScheduledContactStatus | null>(null)

  const unavailability = getContactUnavailability(getContactStatus(contact, false), contact)

    const handleArchiveCallback = () => {
        setIsConfirmingArchive(null)
        if (contact) {
            getContactDetail(contact.id).then((res) => {
                dispatch(updateContact({
                    id: res.data.id,
                    update: res.data,
                }));
            });
        }
    }

  return (
    <Box width="100%">
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        sx={{ pr: 1, py: '4px' }}
      >
        <Box display="flex" alignItems="start">
          <Phone
            sx={{
              color: TAKES_CALLS_COLORS[contact.doYouTakeCalls || 'Default'],
              fontSize: '18px',
              pt: '4px', minWidth: 0, height: 'auto'
            }}
          />
          {contact.doYouTakeCalls === 'Emergency Calls Only' &&
            <PriorityHigh sx={{ mx: '-6px', color: TAKES_CALLS_COLORS['Emergency Calls Only'], fontSize: '18px' }} />
          }
          <Typography sx={{ fontWeight: 600, fontSize: '14px', color: '#1033A5', mx: 1 }}>
            <Highlighter
              searchWords={[search.toLowerCase()]}
              autoEscape={true}
              textToHighlight={getContactName(contact)}
            />
          </Typography>
          {contact.title &&
            <Typography style={{ fontWeight: 600, fontSize: '14px' }}>
              <Highlighter
                searchWords={[search.toLowerCase()]}
                autoEscape={true}
                textToHighlight={contact.title}
              />
            </Typography>
          }
        </Box>
        <Stack display="flex" alignItems="center" direction={contact.title ? 'column' : 'row'}>
          <Button
            sx={{ ml: '5px', p: '4px', minWidth: 0, height: 'auto' }}
            onClick={() => dispatch(setContactForStatusList(contact))}>
            <Tooltip title="Call Status">
              <ListIcon sx={{ fontSize: '20px' }} />
            </Tooltip>
          </Button>
        </Stack>
      </Stack>

      {contact.displayAsAlert &&
        <Box my={1}>
          <ContactStatusAlert
            contactStatus={getContactStatus(contact, true)}
            search={search}
            onEdit={() => dispatch(setContactForEditStatus(contact))}
            onDelete={() => setIsConfirmingArchive(getContactStatus(contact, true))}
            isArchivable={true}
          />
        </Box>
      }

      {unavailability &&
        <Box
          sx={{
            my: '4px', p: '12px',
            width: 'calc(100% - 24px - 2px)',
            border: '1px solid rgba(143, 96, 14, 0.8)',
            borderRadius: '5px',
            backgroundColor: 'rgba(143, 96, 14, 0.5)',
          }}
        >
          <Typography sx={{ fontSize: '13px' }}>
            <Highlighter
              searchWords={[search.toLowerCase()]}
              autoEscape={true}
              textToHighlight={unavailability}
            />
          </Typography>
        </Box>
      }


      <Typography sx={{ fontSize: '13px', fontWeight: 600 }}>
        Takes Calls:&nbsp;
        <span style={{ fontWeight: 'normal' }}>
          <Highlighter
            searchWords={[search.toLowerCase()]}
            autoEscape={true}
            textToHighlight={contact.doYouTakeCalls || 'No'}
          />
        </span>
      </Typography>
      <Typography sx={{ fontSize: '13px', fontWeight: 600 }}>
        Type:&nbsp;
        <span style={{ fontWeight: 'normal' }}>
          <Highlighter
            searchWords={[search.toLowerCase()]}
            autoEscape={true}
            textToHighlight={typesOfCalls}
          />
        </span>
      </Typography>
      {contact.daysAndTimesYouTakeLiveCalls &&
        <Typography sx={{ fontSize: '13px' }}>
          <Highlighter
            searchWords={[search.toLowerCase()]}
            autoEscape={true}
            textToHighlight={contact.daysAndTimesYouTakeLiveCalls}
          />
        </Typography>
      }
        <ContactStatusArchiveDialog
            contactStatus={isConfirmingArchive}
            contact={contact}
            closeArchive={() => setIsConfirmingArchive(null)}
            onArchiveCallback={handleArchiveCallback}
        />
    </Box>
  )
}

export default ContactStatus;
